import React, { useState, useEffect } from 'react';
import Layout from './Layout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

function NetworkingDashboard() {
  const [activeTab, setActiveTab] = useState('firewall'); // 'firewall' or 'networks'
  const [firewallRules, setFirewallRules] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const token = localStorage.getItem('access_token');

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [activeTab]);

  const fetchData = async () => {
    try {
      const instancesRes = await fetch(`${API_URL}/v1/instances`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const instancesData = await instancesRes.json();
      setInstances(instancesData.instances || []);

      if (activeTab === 'firewall') {
        const response = await fetch(`${API_URL}/v1/networking/firewall-rules`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        setFirewallRules(data.firewall_rules || []);
      } else {
        const response = await fetch(`${API_URL}/v1/networking/networks`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        setNetworks(data.networks || []);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const createFirewallRule = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      const response = await fetch(`${API_URL}/v1/networking/firewall-rules`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          instance_id: formData.get('instance_id'),
          name: formData.get('name'),
          protocol: formData.get('protocol'),
          port_range: formData.get('port_range'),
          source: formData.get('source'),
          direction: formData.get('direction'),
          action: formData.get('action')
        })
      });
      if (response.ok) {
        setShowCreateModal(false);
        fetchData();
      }
    } catch (error) {
      alert('Error creating firewall rule: ' + error.message);
    }
  };

  const createNetwork = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      const response = await fetch(`${API_URL}/v1/networking/networks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.get('name'),
          region: formData.get('region'),
          cidr_block: formData.get('cidr_block'),
          description: formData.get('description')
        })
      });
      if (response.ok) {
        setShowCreateModal(false);
        fetchData();
      }
    } catch (error) {
      alert('Error creating network: ' + error.message);
    }
  };

  const deleteFirewallRule = async (ruleId) => {
    if (!window.confirm('Delete this firewall rule?')) return;
    try {
      await fetch(`${API_URL}/v1/networking/firewall-rules/${ruleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      alert('Error deleting rule: ' + error.message);
    }
  };

  const deleteNetwork = async (networkId) => {
    if (!window.confirm('Delete this network?')) return;
    try {
      await fetch(`${API_URL}/v1/networking/networks/${networkId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      alert('Error deleting network: ' + error.message);
    }
  };

  const joinNetwork = async (networkId, instanceId) => {
    try {
      await fetch(`${API_URL}/v1/networking/networks/${networkId}/join`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ instance_id: instanceId })
      });
      fetchData();
    } catch (error) {
      alert('Error joining network: ' + error.message);
    }
  };

  const getInstanceName = (instanceId) => {
    const instance = instances.find(i => i.id === instanceId);
    return instance ? instance.name : instanceId.substring(0, 8);
  };

  return (
    <Layout userType="customer">
      <div className="max-w-7xl mx-auto p-6">
        <h1 className="text-3xl font-bold text-white mb-6">Networking</h1>

        {/* Tabs */}
        <div className="flex space-x-4 mb-6">
          <button
            onClick={() => setActiveTab('firewall')}
            className={`px-6 py-3 rounded-lg font-medium ${
              activeTab === 'firewall'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            Firewall Rules
          </button>
          <button
            onClick={() => setActiveTab('networks')}
            className={`px-6 py-3 rounded-lg font-medium ${
              activeTab === 'networks'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            Private Networks
          </button>
        </div>

        {/* Create Button */}
        <button
          onClick={() => setShowCreateModal(true)}
          className="mb-6 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium"
        >
          + Create {activeTab === 'firewall' ? 'Firewall Rule' : 'Private Network'}
        </button>

        {/* Firewall Rules Table */}
        {activeTab === 'firewall' && (
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Instance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Protocol</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Port</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Source</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Direction</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Action</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {firewallRules.map((rule) => (
                  <tr key={rule.id}>
                    <td className="px-6 py-4 text-white font-medium">{rule.name || '-'}</td>
                    <td className="px-6 py-4 text-gray-400">{getInstanceName(rule.instance_id)}</td>
                    <td className="px-6 py-4 text-gray-400 uppercase">{rule.protocol}</td>
                    <td className="px-6 py-4 text-gray-400">{rule.port_range || 'any'}</td>
                    <td className="px-6 py-4 text-gray-400 font-mono text-sm">{rule.source}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${
                        rule.direction === 'inbound' ? 'bg-blue-900 text-blue-300' : 'bg-purple-900 text-purple-300'
                      }`}>
                        {rule.direction}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${
                        rule.action === 'allow' ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'
                      }`}>
                        {rule.action}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => deleteFirewallRule(rule.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {firewallRules.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                No firewall rules yet. Create your first security rule!
              </div>
            )}
          </div>
        )}

        {/* Private Networks */}
        {activeTab === 'networks' && (
          <div className="space-y-6">
            {networks.map((network) => (
              <div key={network.id} className="bg-gray-800 rounded-lg p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-white">{network.name}</h3>
                    <p className="text-gray-400 text-sm">{network.description}</p>
                  </div>
                  <button
                    onClick={() => deleteNetwork(network.id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    Delete
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <div className="text-gray-400 text-sm">Region</div>
                    <div className="text-white font-medium">{network.region}</div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm">CIDR Block</div>
                    <div className="text-white font-mono">{network.cidr_block}</div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm">Status</div>
                    <div className="text-green-400">{network.status}</div>
                  </div>
                </div>

                <div className="border-t border-gray-700 pt-4">
                  <h4 className="text-white font-medium mb-2">Add Instance to Network</h4>
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        joinNetwork(network.id, e.target.value);
                        e.target.value = '';
                      }
                    }}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  >
                    <option value="">Select instance...</option>
                    {instances.filter(i => i.status === 'running' && i.region === network.region).map(instance => (
                      <option key={instance.id} value={instance.id}>
                        {instance.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
            
            {networks.length === 0 && (
              <div className="bg-gray-800 rounded-lg p-12 text-center text-gray-400">
                No private networks yet. Create your first VPC!
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Firewall Rule Modal */}
      {showCreateModal && activeTab === 'firewall' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-white mb-4">Create Firewall Rule</h3>
            
            <form onSubmit={createFirewallRule}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Instance</label>
                  <select
                    name="instance_id"
                    required
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  >
                    <option value="">Select instance...</option>
                    {instances.map(instance => (
                      <option key={instance.id} value={instance.id}>{instance.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Name (optional)</label>
                  <input
                    name="name"
                    type="text"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                    placeholder="e.g., Allow SSH"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Protocol</label>
                  <select
                    name="protocol"
                    required
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  >
                    <option value="tcp">TCP</option>
                    <option value="udp">UDP</option>
                    <option value="icmp">ICMP</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Port Range</label>
                  <input
                    name="port_range"
                    type="text"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                    placeholder="e.g., 22 or 8000-9000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Source (CIDR)</label>
                  <input
                    name="source"
                    type="text"
                    required
                    defaultValue="0.0.0.0/0"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                    placeholder="0.0.0.0/0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Direction</label>
                  <select
                    name="direction"
                    required
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  >
                    <option value="inbound">Inbound</option>
                    <option value="outbound">Outbound</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Action</label>
                  <select
                    name="action"
                    required
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  >
                    <option value="allow">Allow</option>
                    <option value="deny">Deny</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Network Modal */}
      {showCreateModal && activeTab === 'networks' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-white mb-4">Create Private Network</h3>
            
            <form onSubmit={createNetwork}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Name</label>
                  <input
                    name="name"
                    type="text"
                    required
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Region</label>
                  <select
                    name="region"
                    required
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  >
                    <option value="us-east">US East</option>
                    <option value="us-west">US West</option>
                    <option value="eu-west">EU West</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">CIDR Block</label>
                  <input
                    name="cidr_block"
                    type="text"
                    required
                    defaultValue="10.0.0.0/16"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  />
                  <p className="text-gray-400 text-xs mt-1">e.g., 10.0.0.0/16 or 192.168.0.0/24</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
                  <textarea
                    name="description"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                    rows="3"
                  ></textarea>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default NetworkingDashboard;
